import { useEffect, useMemo, useRef, useState } from "react";
import { _QualityGarmentProperties } from "../../../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../../../core/services/dataBean/useDataBean";
import { toast } from "react-toastify";

interface Props {
  idGarment: number;
  jsonProps?: string;
  onChange?: (value: string) => void;
}

type RowValue = {
  id: number;
  name?: string;
  solicitado?: number;
  tolerancia?: number;
  medido?: number;
  IDMeasurementUnit?: number;
  measurementName?: string;
  satisfy?: "N/A" | "NC" | "C";
};

const isNum = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

export const QualityInspectionEspecialGarmentPropsList = ({
  jsonProps,
  onChange,
  idGarment,
}: Props) => {
  const { instance, lista, setLista } = useDataBean(_QualityGarmentProperties);
  const [values, setValues] = useState<Record<number, RowValue>>({});
  const [listMea, setListMea] = useState<any[]>([]);
  const [dirty, setDirty] = useState(false);
  const lastEmittedRef = useRef<string | null>(null);

  const getLista = () => {
    const props = { IDQualityGarment: idGarment, State: 1 };
    instance
      .getQualityGarmentPropertiesCatalogPorPropiedades(props, null)
      .then((resp: any[]) => setLista(resp ?? []))
      .catch((err: any) => toast.error(err));
  };

  const getMeasurements = () => {
    instance
      .getMeasurementUnitCatalog(null, null)
      .then((resp: any) => setListMea(resp?.List ?? resp ?? []))
      .catch((err: any) => toast.error(err));
  };

  useEffect(() => {
    getMeasurements();
  }, []);

  useEffect(() => {
    if (idGarment) getLista();
    else setLista([]);
    setValues({});
    setDirty(false);
    lastEmittedRef.current = null;
  }, [idGarment]);

  const measurementMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const itm of listMea ?? []) {
      const p = itm?.DataBeanProperties ?? itm;
      const id = Number(p?.IDMeasurementUnit ?? p?.ID);
      if (Number.isFinite(id)) {
        const name: string = p?.Name ?? p?.Nombre ?? String(id);
        m.set(id, name);
      }
    }
    return m;
  }, [listMea]);

  const parsedIncoming: RowValue[] = useMemo(() => {
    if (!jsonProps) return [];
    try {
      const data = JSON.parse(jsonProps);
      if (Array.isArray(data)) {
        return data
          .filter((x) => typeof x?.id === "number")
          .map((x) => {
            const s = typeof x.satisfy === "string" ? (x.satisfy as RowValue["satisfy"]) : undefined;
            return {
              id: x.id,
              solicitado: typeof x.solicitado === "number" ? x.solicitado : undefined,
              tolerancia: typeof x.tolerancia === "number" ? x.tolerancia : undefined, // ✅
              medido: typeof x.medido === "number" ? x.medido : undefined,
              satisfy: s === "C" || s === "NC" || s === "N/A" ? s : undefined,
            };
          });
      }
      return [];
    } catch {
      console.warn("jsonProps no es JSON válido");
      return [];
    }
  }, [jsonProps]);

  useEffect(() => {
    if (!lista || lista.length === 0) return;

    const base: Record<number, RowValue> = {};

    for (const item of lista) {
      const props = item?.DataBeanProperties ?? item;
      const id: number = Number(props?.IDQualityGarmentProperties);
      if (!Number.isFinite(id)) continue;

      const unitId = Number(props?.IDMeasurementUnit);
      base[id] = {
        id,
        name: props?.Name ?? props?.Nombre ?? `Propiedad ${id}`,
        solicitado: typeof props?.PropertieValue === "number" ? props.PropertieValue : undefined,
        IDMeasurementUnit: Number.isFinite(unitId) ? unitId : undefined,
        measurementName: Number.isFinite(unitId) ? measurementMap.get(unitId) : undefined,
        satisfy: "N/A",
      };
    }

    for (const row of parsedIncoming) {
      if (!base[row.id]) base[row.id] = { id: row.id, satisfy: "N/A" };
      base[row.id] = {
        ...base[row.id],
        solicitado: row.solicitado ?? base[row.id].solicitado,
        tolerancia: row.tolerancia ?? base[row.id].tolerancia,
        medido: row.medido ?? base[row.id].medido,
        satisfy: row.satisfy ?? base[row.id].satisfy ?? "N/A",
      };
    }

    setValues(base);
    setDirty(false);
  }, [lista, parsedIncoming, measurementMap]);

  useEffect(() => {
    if (!onChange || !dirty) return;
    const payload = Object.values(values).map(
      ({ id, name, solicitado, tolerancia, medido, IDMeasurementUnit, measurementName, satisfy }) => ({
        id,
        name,
        solicitado,
        tolerancia,
        medido,
        IDMeasurementUnit,
        measurementName,
        satisfy,
      })
    );
    const json = JSON.stringify(payload);
    if (json !== lastEmittedRef.current) {
      lastEmittedRef.current = json;
      onChange(json);
    }
  }, [values, dirty, onChange]);

  const handleChange = (
    id: number,
    field: "solicitado" | "tolerancia" | "medido" | "satisfy",
    value: string
  ) => {
    setValues((prev) => {
      const current = (prev[id] ?? { id, satisfy: "N/A" }) as RowValue;

      if (field === "satisfy") {
        const next: RowValue = { ...current, satisfy: (value as RowValue["satisfy"]) || "N/A" };
        return { ...prev, [id]: next };
      }

      const num = value === "" ? undefined : Number(value);
      const next: RowValue = {
        ...current,
        [field]: Number.isFinite(num) ? num : undefined,
      } as RowValue;

      if (isNum(next.solicitado) && isNum(next.tolerancia) && isNum(next.medido)) {
        const min = next.solicitado - next.tolerancia;
        const max = next.solicitado + next.tolerancia;
        next.satisfy = next.medido >= min && next.medido <= max ? "C" : "NC";
      } else {
        next.satisfy = "N/A";
      }

      return { ...prev, [id]: next };
    });
    setDirty(true);
  };

  const rows = useMemo(() => Object.values(values), [values]);

  return (
    <div className="row mt-2">
      <div className="col-md-12">
        <h4>Dimensiones</h4>
        <hr />
      </div>

      {rows.length === 0 && (
        <div className="col-md-12">
          <em>No hay propiedades para esta prenda.</em>
        </div>
      )}

      {rows.map((row) => (
        <div className="col-md-6" key={row.id}>
          <div className="row align-items-center">
            <div className="col-md-3">
              <label className="d-block">{row.name ?? `Propiedad ${row.id}`}</label>
              {row.measurementName && <small className="text-muted">{row.measurementName}</small>}
            </div>

            <div className="col-md-2">
              <label>Exigida</label>
              <input
                type="number"
                value={row.solicitado ?? ""}
                onChange={(e) => handleChange(row.id, "solicitado", e.target.value)}
                className="form-control"
                inputMode="decimal"
              />
            </div>

            <div className="col-md-2">
              <label>Tolerancia</label>
              <input
                type="number"
                value={row.tolerancia ?? ""}
                onChange={(e) => handleChange(row.id, "tolerancia", e.target.value)}
                className="form-control"
                inputMode="decimal"
              />
            </div>

            <div className="col-md-2">
              <label>Presentada</label>
              <input
                type="number"
                value={row.medido ?? ""}
                onChange={(e) => handleChange(row.id, "medido", e.target.value)}
                className="form-control"
                inputMode="decimal"
              />
            </div>

            <div className="col-md-3">
              <label>¿Cumple?</label>
              <select
                value={row.satisfy ?? "N/A"}
                onChange={(e) => handleChange(row.id, "satisfy", e.target.value)}
                className="form-select"
              >
                <option value="N/A">No Aplica</option>
                <option value="NC">No Cumple</option>
                <option value="C">Cumple</option>
              </select>
            </div>
          </div>
        </div>
      ))}

      <div className="col-md-12">
        <hr />
      </div>
    </div>
  );
};
